import { getPosition } from './base.utils.js';

const sidebarControllers = new WeakMap();

const createEmptyController = () => ({
  update() {},
  destroy() {},
});

export const followingSidebar = ({
  sidebar,
  contentSticky,
  sidebarInnerSelector = '.following-sidebar-inner',
  isHeaderCalc = false,
  gap = 130,
  bottomGap = gap,
}) => {
  const sidebarInner = sidebar?.querySelector(sidebarInnerSelector);

  if (!sidebar || !contentSticky || !sidebarInner) {
    return createEmptyController();
  }

  sidebarControllers.get(sidebar)?.destroy();

  let sidebarBuffer = Array.from(sidebar.children).find((element) =>
    element.classList.contains('following-sidebar-buffer')
  );

  if (!sidebarBuffer) {
    sidebarBuffer = document.createElement('div');
    sidebarBuffer.classList.add('following-sidebar-buffer');
    sidebar.insertAdjacentElement('afterbegin', sidebarBuffer);
  }

  const header = document.querySelector('header');
  const headerHeight = isHeaderCalc && header ? header.offsetHeight : 0;
  let lastScrollTop = 0;
  let isScrollingDown = null;
  let isDestroyed = false;
  let isSidebarHigherViewport =
    sidebarInner.getBoundingClientRect().height >
    window.innerHeight - headerHeight;

  const updateSidebarHeightState = () => {
    isSidebarHigherViewport =
      sidebarInner.getBoundingClientRect().height >
      window.innerHeight - headerHeight;
  };

  const calcHeightOfSidebar = (scrollingDown) => {
    const contentStickyTop = getPosition(contentSticky).y;
    const sidebarInnerTop = getPosition(sidebarInner).y;

    sidebarBuffer.style.height = `${Math.max(0, sidebarInnerTop - contentStickyTop)}px`;

    if (scrollingDown) {
      sidebarInner.style.top = '';
      sidebarInner.style.bottom = `${
        -headerHeight -
        gap +
        window.innerHeight -
        sidebarInner.getBoundingClientRect().height
      }px`;
    } else {
      sidebarInner.style.bottom = '';
      sidebarInner.style.top = `${
        Math.min(
          window.innerHeight - sidebarInner.getBoundingClientRect().height,
          headerHeight
        ) - bottomGap
      }px`;
    }

    updateSidebarHeightState();
  };

  const resetSticky = () => {
    sidebarInner.style.bottom = '';
    sidebarInner.style.top = `${headerHeight + gap}px`;
    sidebarBuffer.style.height = '0';
  };

  const update = () => {
    if (isDestroyed) {
      return;
    }

    updateSidebarHeightState();

    if (!isSidebarHigherViewport) {
      resetSticky();
      return;
    }

    calcHeightOfSidebar(isScrollingDown ?? false);
  };

  const handleScroll = () => {
    const st = window.scrollY || document.documentElement.scrollTop;

    if (!isSidebarHigherViewport) {
      resetSticky();
      lastScrollTop = st <= 0 ? 0 : st;
      return;
    }

    if (st > lastScrollTop && !isScrollingDown) {
      calcHeightOfSidebar(isScrollingDown);
      isScrollingDown = true;
    } else if (st < lastScrollTop && isScrollingDown) {
      calcHeightOfSidebar(isScrollingDown);
      isScrollingDown = false;
    }

    lastScrollTop = st <= 0 ? 0 : st;
  };

  let windowWidth = window.innerWidth;

  const handleResize = () => {
    if (windowWidth === window.innerWidth) {
      return;
    }

    windowWidth = window.innerWidth;
    update();
  };

  const destroy = () => {
    if (isDestroyed) {
      return;
    }

    isDestroyed = true;
    window.removeEventListener('scroll', handleScroll);
    window.removeEventListener('resize', handleResize);
    sidebarInner.style.bottom = '';
    sidebarInner.style.top = '';
    sidebarInner.style.position = '';
    sidebarBuffer.remove();
    sidebarControllers.delete(sidebar);
  };

  const controller = {
    update,
    destroy,
  };

  sidebarInner.style.position = 'sticky';
  window.addEventListener('scroll', handleScroll);
  window.addEventListener('resize', handleResize);
  update();

  sidebarControllers.set(sidebar, controller);

  return controller;
};
